import React, { createContext, useContext, useMemo } from 'react'

import { node, string, bool } from 'prop-types'

/**
 * @typedef {Object} PreviewModeContextValue
 * @property {boolean} isPreview - Whether the page is in preview mode
 * @property {string|null} editUrl - URL to the edit page for this item
 */

/**
 * Context for preview mode state.
 * Used to disable commerce actions and show edit buttons in preview mode.
 */
export const PreviewModeContext = createContext({
  isPreview: false,
  editUrl: null,
})

/**
 * Hook to access preview mode context values.
 * Returns default values (isPreview: false) when used outside provider.
 *
 * @returns {PreviewModeContextValue}
 */
export function usePreviewMode() {
  return useContext(PreviewModeContext)
}

/**
 * Provider component that wraps children with preview mode context.
 *
 * @param {Object} props
 * @param {boolean} props.isPreview - Whether preview mode is active
 * @param {string} props.editUrl - URL to the item edit page
 * @param {React.ReactNode} props.children - Child components
 */
export function PreviewModeProvider({ isPreview = false, editUrl = null, children }) {
  const value = useMemo(() => ({
    isPreview,
    editUrl,
  }), [isPreview, editUrl])

  return (
    <PreviewModeContext.Provider value={value}>
      {children}
    </PreviewModeContext.Provider>
  )
}

PreviewModeProvider.propTypes = {
  isPreview: bool,
  editUrl: string,
  children: node.isRequired,
}
